import React, { useState, useEffect } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useDropzone } from 'react-dropzone';
import { toast } from 'react-hot-toast';
import { UPDATE_OWN_PROFILE, UPDATE_OWN_PASSWORD } from '../graphql/mutations';
import { GET_CURRENT_USER } from '../graphql/queries';
import { ArrowBack as ArrowLeft } from '@mui/icons-material';
import { API_URL } from '../config';
import { compressImage, validateImageFile } from '../utils/image-utils';
import SafeAvatar from '../components/SafeAvatar';
import LocationTrackingSettingsSection from '../components/location/LocationTrackingSettingsSection';
import {
  GET_MY_NOTIFICATION_PREFERENCES,
  UPDATE_MY_NOTIFICATION_PREFERENCE,
  UPDATE_USER_NOTIFICATION_PREFERENCES
} from '../graphql/notifications';

// Define RESET_FIRST_LOGIN mutation if needed
import { gql } from '@apollo/client';

const RESET_FIRST_LOGIN = gql`
  mutation ResetFirstLogin {
    resetFirstLogin {
      success
    }
  }
`;

const isPersistableAvatarUrl = (avatarUrl) => {
  if (!avatarUrl || typeof avatarUrl !== 'string') return false;

  const trimmedAvatarUrl = avatarUrl.trim();
  if (!trimmedAvatarUrl || trimmedAvatarUrl === 'pending-upload') return false;
  if (trimmedAvatarUrl.startsWith('data:image/') || trimmedAvatarUrl.startsWith('blob:')) return false;

  return (
    trimmedAvatarUrl.startsWith('http://') ||
    trimmedAvatarUrl.startsWith('https://') ||
    trimmedAvatarUrl.startsWith('avatars/')
  );
};

// Base input styles - replace all input field classes with this improved version
const baseInputStyles = "px-4 py-2.5 h-11 w-full border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 focus:border-sky-500 focus:ring-1 focus:ring-sky-500 disabled:bg-gray-50 disabled:text-gray-500 disabled:border-gray-200 text-sm transition-colors";

const notificationTypeLabels = {
  SCHEDULE_UPDATE: 'Schedule Updates',
  JOB_DATE_CHANGE: 'Job Date Changes',
  JOB_CANCELLATION: 'Job Cancellations',
  READY_FOR_INSPECTION: 'Ready for Inspection',
  INSPECTION_REPORT: 'Inspection Reports',
  WORK_ORDER: 'Work Orders',
  WORK_ORDER_ASSIGNMENT: 'Work Order Assignments',
  WORK_ORDER_COMPLETED: 'Work Order Completed',
  ESCALATED_WORK_ORDER: 'Escalated Work Orders',
  WORK_ORDER_COMMENT_CALL_CENTER: 'Work Order Comment (Reservation Team)',
  LOST_ITEM: 'Lost Items',
  USER_INVITATION: 'User Invitations',
  GUEST_ORDERS: 'Guest Orders',
  TIME_OFF_REQUEST: 'Team Availability Requests',
  TIME_OFF_DECISION: 'Time-Off Decisions',
};

export default function UserSettings() {
  const { user, refreshUser, updatePasswordFirstTime } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const usedGoogleSso = location.state?.usedGoogleSso === true;

  // Add state for password change section visibility and completion status
  const [needsPasswordChange, setNeedsPasswordChange] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordChangeLoading, setPasswordChangeLoading] = useState(false);

  // Add state to track workflow steps
  const [currentStep, setCurrentStep] = useState(1); // 1 = Profile Info, 2 = Password Change

  // Add state for profile setup completion
  const [setupCompleted, setSetupCompleted] = useState(false);



  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    avatarUrl: '',
    smsNotifications: true,
    emailNotifications: true,
    notificationEmailOverride: ''
  });
  const [isSavingNotificationEmail, setIsSavingNotificationEmail] = useState(false);

  // Initialize validation state
  const [validationState, setValidationState] = useState({
    hasFirstName: !!user.firstName,
    hasLastName: !!user.lastName,
    hasPhoneNumber: !!user.phoneNumber,
    hasAvatar: !!user.avatarUrl,
    isComplete: !!user.firstName && !!user.lastName && !!user.phoneNumber && !!user.avatarUrl
  });

  const [imagePreview, setImagePreview] = useState(null);
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [passwordsMatch, setPasswordsMatch] = useState(true);

  // Add reset first login mutation
  const [resetFirstLogin] = useMutation(RESET_FIRST_LOGIN);
  const [updateOwnProfile, { error: updateError }] = useMutation(UPDATE_OWN_PROFILE);
  const [updatePassword, { error: passwordError }] = useMutation(UPDATE_OWN_PASSWORD);
  const [savingNotificationKey, setSavingNotificationKey] = useState('');

  const {
    data: notificationPreferencesData,
    loading: notificationPreferencesLoading,
    error: notificationPreferencesError,
    refetch: refetchNotificationPreferences
  } = useQuery(GET_MY_NOTIFICATION_PREFERENCES, {
    skip: !user,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    errorPolicy: 'all'
  });

  const [updateMasterNotificationPreferences] = useMutation(UPDATE_USER_NOTIFICATION_PREFERENCES);
  const [updateMyNotificationPreference] = useMutation(UPDATE_MY_NOTIFICATION_PREFERENCE);

  // State for password update success
  const [passwordUpdateSuccess, setPasswordUpdateSuccess] = useState(false);
  // State for regular password change
  const [regularPasswordLoading, setRegularPasswordLoading] = useState(false);
  const [regularPasswordSuccess, setRegularPasswordSuccess] = useState(false);

  // Add missing state variables
  const [submitting, setSubmitting] = useState(false);
  const [avatarFile, setAvatarFile] = useState(null);
  const [showPasswordSwitch, setShowPasswordSwitch] = useState(false);
  const [switchingAuth, setSwitchingAuth] = useState(false);

  // Add refetch function from useQuery
  const { refetch } = useQuery(GET_CURRENT_USER);

  // Handle file uploads with dropzone
  const onDrop = async (acceptedFiles) => {
    if (acceptedFiles && acceptedFiles.length > 0) {
      let file = acceptedFiles[0];

      try {
        // Validate the file first
        const validation = validateImageFile(file);
        if (!validation.isValid) {
          toast.error(validation.error);
          return;
        }

        // Check if the file is a HEIC/HEIF file using multiple detection methods
        const isHeic =
          file.type === 'image/heic' ||
          file.type === 'image/heif' ||
          file.name.toLowerCase().endsWith('.heic') ||
          file.name.toLowerCase().endsWith('.heif');

        if (isHeic) {
          // Show loading toast
          toast.loading("Converting iPhone HEIC image...", { id: "heic-conversion" });

          try {
            // Dynamically import heic2any to prevent issues with server-side rendering
            // and to catch import errors separately from conversion errors
            const heic2any = (await import('heic2any')).default;

            // Convert HEIC to JPEG with error handling
            const jpegBlob = await heic2any({
              blob: file,
              toType: "image/jpeg",
              quality: 0.8,
              // Add a reasonable timeout to prevent infinite processing
              timeout: 30000
            });

            // Ensure we have a valid blob result
            if (!jpegBlob) {
              throw new Error('Conversion resulted in an empty file');
            }

            // Create a new file from the JPEG blob
            file = new File(
              [jpegBlob],
              file.name.replace(/\.(heic|heif)$/i, '.jpg'),
              { type: 'image/jpeg' }
            );

            toast.success("HEIC image converted successfully", { id: "heic-conversion" });
          } catch (conversionError) {
            console.error("Error converting HEIC image:", conversionError);

            // Clear the loading toast
            toast.dismiss("heic-conversion");

            // Show detailed error and suggestion to the user
            toast.error(
              "Unable to convert HEIC image. Please convert it to JPEG or PNG before uploading, or try a different image.",
              { duration: 5000 }
            );

            return;
          }
        }

                // Compress the image to optimize file size and performance
        toast.loading("Optimizing image...", { id: "image-compression" });
        
        try {
          const compressedFile = await compressImage(file, {
            maxWidth: 400,
            maxHeight: 400,
            quality: 0.85,
            outputFormat: 'image/jpeg'
          });

          file = compressedFile;
          toast.success("Image optimized successfully", { id: "image-compression" });
        } catch (compressionError) {
          toast.dismiss("image-compression");
          toast.info("Using original image");
        }

        // Store the file for later upload
        setAvatarFile(file);

        // Create a preview with error handling
        const reader = new FileReader();
        reader.onload = (e) => {
          if (e.target?.result) {
            setImagePreview(e.target.result);
          } else {
            toast.error("Failed to generate image preview");
          }
        };
        reader.onerror = () => {
          toast.error("Failed to read the image file");
        };
        reader.readAsDataURL(file);

        // Update the form data
        setFormData(prev => ({
          ...prev,
          avatarUrl: 'pending-upload' // Will be replaced with the actual URL after upload
        }));

        toast.success("Image selected. Save your profile to upload it.");
      } catch (error) {
        console.error("Error processing uploaded file:", error);
        toast.error("There was an error processing your image. Please try a different file or format.");
      }
    }
  };

  // Handle first-time password change submission
  const handlePasswordChangeSubmit = async (e) => {
    e.preventDefault();

    // Validate the passwords
    if (newPassword.length < 8) {
      toast.error("Password must be at least 8 characters long");
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }

    try {
      setPasswordChangeLoading(true);

      // Call the updatePasswordFirstTime function from AuthContext
      const result = await updatePasswordFirstTime({
        variables: {
          password: newPassword
        }
      });

      // Handle the response - updatePasswordFirstTime returns {success: true} directly
      if (result && result.success) {
        // Set success message
        setPasswordUpdateSuccess(true);
        toast.success("Password updated successfully!");

        // Update the needsPasswordChange state to false
        setNeedsPasswordChange(false);

        // Update the validation state passwordChanged flag
        setValidationState(prev => ({
          ...prev,
          passwordChanged: true,
          isComplete: prev.hasFirstName && prev.hasLastName && prev.hasPhoneNumber && prev.hasAvatar && true
        }));

        // Reset password fields
        setNewPassword('');
        setConfirmPassword('');

        // Important: Set setup as completed and make sure it's visible
        setSetupCompleted(true);

        // Store the completion status in session storage as a backup
        sessionStorage.setItem('profileSetupCompleted', 'true');

        try {
          // Refresh user data to get updated status but don't redirect
          await refreshUser();
        } catch (error) {
          console.error('Error refreshing user:', error);
        }
      } else {
        console.error('Password update failed:', result?.error || 'Unknown error');
        toast.error(result?.error || "Error updating password");
      }
    } catch (error) {
      console.error("Error updating password:", error);
      toast.error(error.message || "Failed to update password");
    } finally {
      setPasswordChangeLoading(false);
    }
  };

  // Function to handle password change for regular users
  const handlePasswordChange = async () => {
    // Validate the password fields
    if (!formData.currentPassword) {
      toast.error("Current password is required");
      return;
    }

    if (!formData.newPassword || formData.newPassword.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      toast.error("New passwords don't match");
      return;
    }

    try {
      // Show loading state
      setRegularPasswordLoading(true);
      setRegularPasswordSuccess(false);

      // Call the updatePassword mutation
      const { data } = await updatePassword({
        variables: {
          currentPassword: formData.currentPassword,
          newPassword: formData.newPassword
        }
      });

      if (data?.updateOwnPassword) {
        // Success - reset the password fields
        setFormData(prev => ({
          ...prev,
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        }));

        // Show success message
        setRegularPasswordSuccess(true);
        toast.success("Password updated successfully");
      }
    } catch (error) {
      console.error("Error updating password:", error);
      toast.error(error.message || "Failed to update password");
    } finally {
      setRegularPasswordLoading(false);
    }
  };

  const handleSwitchToPassword = async () => {
    setSwitchingAuth(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/switch-to-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error('Failed to switch authentication method');
      }
      localStorage.setItem('last-auth-method', 'password');
      setShowPasswordSwitch(true);
      await refreshUser();
      toast.success('Switched to password login. You can now set a new password below.');
    } catch (err) {
      toast.error(err.message || 'Failed to switch authentication method');
    } finally {
      setSwitchingAuth(false);
    }
  };

  // Function to navigate to dashboard
  const goToDashboard = () => {
    // Clear the session storage flag before navigating
    sessionStorage.removeItem('profileSetupCompleted');
    navigate('/dashboard');
  };

  // Handle admin override functionality
  const handleAdminOverride = async () => {
    try {

      // Force the validation state to complete regardless of actual profile state
      setValidationState({
        hasFirstName: true,
        hasLastName: true,
        hasPhoneNumber: true,
        hasAvatar: true,
        isComplete: true
      });

      toast.success('Profile completion check bypassed through admin override');

      // We still try to reset the firstLogin flag as a courtesy
      try {
        await resetFirstLogin();
      } catch (err) {
        // Note: Could not reset first login flag, but continuing with override
      }

      // Even though we update the local validation state, also refresh user data
      await refreshUser();

      // Allow time for toast to be seen
      const timeoutId = setTimeout(() => {
        navigate('/dashboard');
      }, 1500);
      
      // Store timeout ID for cleanup
      return () => clearTimeout(timeoutId);
    } catch (err) {
      console.error('Error processing admin override:', err);
      toast.error(err.message || 'Failed to process admin override');
    }
  };

  // Set initial form data based on user object
  useEffect(() => {
    if (user) {

      // Check for saved completion status in session storage
      const completedFromSession = sessionStorage.getItem('profileSetupCompleted') === 'true';
      if (completedFromSession) {
        setSetupCompleted(true);
        setCurrentStep(2); // Ensure we're on the password step
      }

      setNeedsPasswordChange(user.isFirstLogin && !usedGoogleSso);

      // Initialize the step based on first login status - FIXED VERSION
      // First-time users start at step 1 (profile)
      // We only modify the step once at initial load, not on every render
      if (user.isFirstLogin) {
        // For first login users, we start at step 1 (unless overridden by session)
        if (!completedFromSession) {
          setCurrentStep(1);
        }
      } else {
        // For returning users who have completed setup, always start at profile page
        setCurrentStep(1);
      }

      // Initialize form data with user values
      setFormData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        phoneNumber: user.phoneNumber || '',
        avatarUrl: user.avatarUrl || '',
        smsNotifications: user.smsNotifications ?? true,
        emailNotifications: user.emailNotifications ?? true,
        notificationEmailOverride: user.notificationEmailOverride || ''
      });

      // Set image preview if user has an avatar
      if (user.avatarUrl) {
        setImagePreview(user.avatarUrl);
      }

      // Update validation state based on user data
      const hasFirstName = !!user.firstName;
      const hasLastName = !!user.lastName;
      const hasPhoneNumber = !!user.phoneNumber;
      const hasAvatar = !!user.avatarUrl;
      const passwordChanged = !user.isFirstLogin;
      const isComplete = hasFirstName && hasLastName && hasPhoneNumber && hasAvatar && passwordChanged;

      setValidationState({
        hasFirstName,
        hasLastName,
        hasPhoneNumber,
        hasAvatar,
        passwordChanged,
        isComplete
      });

    }
    // Only re-run this effect when the user object changes, not when currentStep changes
  }, [user, refreshUser]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Check if passwords match when either password field changes
    if (name === 'newPassword' || name === 'confirmPassword') {
      const isMatch = name === 'newPassword'
        ? value === formData.confirmPassword
        : formData.newPassword === value;

      if (value && (formData.newPassword || formData.confirmPassword)) {
        setPasswordsMatch(isMatch);
      } else {
        setPasswordsMatch(true);
      }
    }
  };

  const handleToggleMasterNotification = async (field) => {
    const nextValue = !formData[field];
    setFormData(prev => ({
      ...prev,
      [field]: nextValue
    }));

    try {
      await updateMasterNotificationPreferences({
        variables: {
          emailEnabled: field === 'emailNotifications' ? nextValue : undefined,
          smsEnabled: field === 'smsNotifications' ? nextValue : undefined
        }
      });
      await refreshUser();
      toast.success(`${field === 'emailNotifications' ? 'Email' : 'SMS'} notifications ${nextValue ? 'enabled' : 'disabled'}`);
    } catch (error) {
      setFormData(prev => ({
        ...prev,
        [field]: !nextValue
      }));
      toast.error(error.message || 'Failed to update notification preference');
    }
  };

  const handleSaveNotificationEmailOverride = async () => {
    const trimmedOverride = (formData.notificationEmailOverride || '').trim();
    if (trimmedOverride && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedOverride)) {
      toast.error('Please enter a valid notification email address');
      return;
    }
    setIsSavingNotificationEmail(true);
    try {
      await updateOwnProfile({
        variables: {
          input: {
            firstName: formData.firstName || user.firstName,
            lastName: formData.lastName || user.lastName || '',
            notificationEmailOverride: trimmedOverride
          }
        }
      });
      await refreshUser();
      toast.success(
        trimmedOverride
          ? 'Notification emails will now go to your override address'
          : 'Notification email override cleared'
      );
    } catch (error) {
      toast.error(error.message || 'Failed to save notification email');
    } finally {
      setIsSavingNotificationEmail(false);
    }
  };

  const handleToggleAlertPreference = async (preference, field) => {
    const nextValue = !preference[field];
    const savingKey = `${preference.notificationType}:${field}`;
    setSavingNotificationKey(savingKey);

    try {
      await updateMyNotificationPreference({
        variables: {
          notificationType: preference.notificationType,
          emailEnabled: field === 'emailEnabled' ? nextValue : undefined,
          smsEnabled: field === 'smsEnabled' ? nextValue : undefined
        }
      });
      await refetchNotificationPreferences();
      toast.success(`${notificationTypeLabels[preference.notificationType] || preference.notificationType.replace(/_/g, ' ')} ${field === 'emailEnabled' ? 'email' : 'SMS'} ${nextValue ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error(error.message || 'Failed to update alert preference');
    } finally {
      setSavingNotificationKey('');
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.heic', '.heif']
    },
    maxFiles: 1,
    multiple: false
  });

  // Handle form submission - modified to update workflow steps
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    const errors = {};
    if (!formData.firstName?.trim()) {
      errors.firstName = "First name is required";
    }
    if (!formData.lastName?.trim()) {
      errors.lastName = "Last name is required";
    }
    if (!formData.phoneNumber?.trim()) {
      errors.phoneNumber = "Phone number is required";
    }
    if (user.isFirstLogin && !avatarFile && !isPersistableAvatarUrl(formData.avatarUrl)) {
      errors.avatar = "Profile photo is required";
      toast.error("Please upload a profile photo");
    }

    if (Object.keys(errors).length > 0) {
      return;
    }

    setSubmitting(true);

    try {
      let avatarUrlValue = formData.avatarUrl;
      let uploadedAvatarUrl = null;

      // Handle avatar upload first if there's a file
      if (avatarFile) {
        try {
          const uploadedUrl = await handleAvatarUpload();
          if (uploadedUrl) {
            avatarUrlValue = uploadedUrl;
            uploadedAvatarUrl = uploadedUrl;
            toast.success("Avatar uploaded successfully!");
          } else {
            toast.error("Avatar upload failed, will save other profile info");
          }
        } catch (error) {
          toast.error(`Avatar upload failed: ${error.message}`);
          // Keep existing avatar URL, don't change it
        }
      }

      // Now send the update request to the server
      const profileInput = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        phoneNumber: formData.phoneNumber || null,
        smsNotifications: formData.smsNotifications,
        emailNotifications: formData.emailNotifications,
        notificationEmailOverride: (formData.notificationEmailOverride || '').trim()
      };

      if (uploadedAvatarUrl) {
        profileInput.avatarUrl = uploadedAvatarUrl;
      } else if (isPersistableAvatarUrl(formData.avatarUrl) && formData.avatarUrl !== user.avatarUrl) {
        profileInput.avatarUrl = formData.avatarUrl;
      }
      
      const { data } = await updateOwnProfile({
        variables: {
          input: profileInput
        },
      });

              if (data?.updateOwnProfile) {
          // Show success message
          setUpdateSuccess(true);
          toast.success("Profile updated successfully");

          // Update form data with the saved avatar URL immediately
          setFormData(prev => ({
            ...prev,
            avatarUrl: isPersistableAvatarUrl(avatarUrlValue) ? avatarUrlValue : prev.avatarUrl
          }));

          // Clear the preview and file AFTER updating formData
          setImagePreview(null);
          setAvatarFile(null);

        if (user.isFirstLogin && currentStep === 1) {
          const profileComplete =
            formData.firstName &&
            formData.lastName &&
            formData.phoneNumber &&
            (isPersistableAvatarUrl(avatarUrlValue) || isPersistableAvatarUrl(formData.avatarUrl));
          if (profileComplete) {
            setValidationState(prev => ({
              ...prev,
              hasFirstName: true,
              hasLastName: true,
              hasPhoneNumber: true,
              hasAvatar: true
            }));
            if (usedGoogleSso) {
              setSetupCompleted(true);
              setPasswordUpdateSuccess(true);
              setNeedsPasswordChange(false);
              sessionStorage.setItem('profileSetupCompleted', 'true');
              setCurrentStep(2);
              try {
                await resetFirstLogin();
              } catch (err) {
                // Non-critical — profile was saved successfully
              }
              await refreshUser();
            } else {
              setCurrentStep(2);
              const timeoutId = setTimeout(() => {
                scrollToElement('password-section');
              }, 300);
              return () => clearTimeout(timeoutId);
            }
          }
        }

        // Refresh user data with the updated query 
        const result = await refetch();
        if (result.data?.me) {
          // Update form data with the refreshed user data
          // IMPORTANT: Preserve the avatarUrlValue we just uploaded, don't overwrite it
          setFormData(prev => ({
            ...prev,
            ...result.data.me,
            avatarUrl: isPersistableAvatarUrl(avatarUrlValue) ? avatarUrlValue : result.data.me.avatarUrl // Keep the new upload
          }));
          
          // Refresh the global user context so Header updates immediately
          // Wait a bit to ensure database has been updated
          setTimeout(async () => {
            await refreshUser();
          }, 500);
        }
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error(error.message || "Failed to update profile");
    } finally {
      setSubmitting(false);
    }
  };

  // Get current scroll position
  const scrollToElement = (elementId) => {
    const element = document.getElementById(elementId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // If user isn't loaded yet
  if (!user) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin h-10 w-10 border-4 border-blue-500 rounded-full border-t-transparent"></div>
      </div>
    );
  }


  // Add the function to handle avatar upload
  const handleAvatarUpload = async () => {
    if (!avatarFile) return null;

    try {
      // Step 1: Get upload URL from backend using cookies like the rest of the app
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
      
      const response = await fetch(`${API_URL}/api/users/get-avatar-upload-url`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include', // Use cookies for authentication like AuthContext
        body: JSON.stringify({
          fileName: avatarFile.name,
          fileType: avatarFile.type
        }),
        signal: controller.signal
      });

      clearTimeout(timeoutId);
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to get upload URL: ${response.status} ${response.statusText} - ${errorText}`);
      }

      const { uploadUrl, imageUrl, headers } = await response.json();

      // Step 2: Upload file to S3
      // Add timeout to S3 upload as well
      const uploadController = new AbortController();
      const uploadTimeoutId = setTimeout(() => uploadController.abort(), 60000); // 60 second timeout for S3
      
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        headers: headers, // Don't add x-amz-acl header - it's already in the pre-signed URL
        body: avatarFile,
        signal: uploadController.signal
      });

      clearTimeout(uploadTimeoutId);

      if (!uploadResponse.ok) {
        throw new Error(`Failed to upload file: ${uploadResponse.statusText}`);
      }

      toast.success("Avatar uploaded successfully!");
      return imageUrl;

    } catch (error) {
      // Handle specific error types
      if (error.name === 'AbortError') {
        toast.error('Upload timed out. Please try again.');
      } else if (error.message.includes('408')) {
        toast.error('Upload request timed out. Please try again.');
      } else {
        toast.error(`Failed to upload avatar: ${error.message}`);
      }
      
      return null;
    }
  };

  const notificationPreferences = notificationPreferencesData?.myNotificationPreferences || [];

  return (
    <div className="container mx-auto p-4 max-w-3xl">
      {/* Back button */}
      <div className="mb-6">
        <Link
          to="/dashboard"
          className="inline-flex items-center text-gray-200 hover:text-white lg:text-gray-700 lg:hover:text-gray-900 transition-colors"
        >
          <ArrowLeft className="mr-1" />
          <span>Back to Dashboard</span>
        </Link>
      </div>

      {/* First Login Welcome Message */}
      {user.isFirstLogin && (
        <div className="mb-8 bg-white shadow-sm rounded-lg overflow-hidden border border-blue-300">
          <div className="px-4 py-5 bg-blue-50 text-blue-800 flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <h3 className="text-lg font-medium leading-6">Welcome to Your Account Setup</h3>
              {usedGoogleSso ? (
                <p className="mt-1 text-sm">
                  You signed in with Google — no password reset is needed. Please complete your profile information below (name, phone, and profile photo).
                  If you ever need to change your password, you can do so through your Google account.
                </p>
              ) : (
                <p className="mt-1 text-sm">
                  Please complete your profile information first, then you'll set a new password.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Google SSO badge for first-login users */}
      {user.isFirstLogin && usedGoogleSso && (
        <div className="mb-6 bg-white shadow-sm rounded-lg overflow-hidden border border-green-300">
          <div className="px-4 py-3 bg-green-50 text-green-800 flex items-center">
            <svg className="h-5 w-5 mr-2 text-green-600 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            <p className="text-sm font-medium">Signed in with Google — password reset not required</p>
          </div>
        </div>
      )}

      {/* Password update success message */}
      {passwordUpdateSuccess && (
        <div className="mb-8 bg-white shadow-sm rounded-lg overflow-hidden border border-green-300">
          <div className="px-4 py-5 bg-green-50 text-green-800 flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <div>
              <h3 className="text-lg font-medium leading-6">Password Updated Successfully!</h3>
              <p className="mt-1 text-sm">
                Your password has been updated. You will be redirected to the dashboard shortly.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Progress Steps for First Login */}
      {user.isFirstLogin && (
        <div className="mb-8">
          {currentStep === 1 && (
            <div className="bg-blue-50 rounded-lg p-4 border-l-4 border-blue-500">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-blue-800">
                    {usedGoogleSso ? 'Complete Your Profile' : 'Step 1: Complete Your Profile'}
                  </h3>
                  <p className="text-xs text-blue-700 mt-1">
                    {usedGoogleSso
                      ? 'Fill in your profile information and upload a profile picture. No password change needed with Google sign-in.'
                      : 'Please fill in your profile information and upload a profile picture'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {currentStep === 2 && !usedGoogleSso && !setupCompleted && !passwordUpdateSuccess && (
            <div className="bg-blue-50 rounded-lg p-4 border-l-4 border-blue-500">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-blue-800">Step 2: Set Your Password</h3>
                  <p className="text-xs text-blue-700 mt-1">
                    Create a secure password for your account
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Page title - hide for first login flow */}
      {!user.isFirstLogin && (
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-100 lg:text-gray-900">User Settings</h1>
          <p className="text-gray-300 lg:text-gray-600 mt-1">Manage your profile and account settings</p>
        </div>
      )}

      {/* Profile Information Section - Always show but may be disabled */}
      <div className={`mb-6 ${currentStep === 2 && !setupCompleted && !passwordUpdateSuccess ? "opacity-50 pointer-events-none" : ""}`}>
        <div className="bg-white shadow-sm rounded-lg overflow-hidden border border-gray-200">
          <div className="px-4 py-5 sm:px-6 bg-gray-50 border-b border-gray-200">
            <h3 className="text-lg font-medium leading-6 text-gray-900">Profile Information</h3>
            <p className="mt-1 text-sm text-gray-500">
              Update your personal information and preferences
            </p>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2" id="firstName-section">
                <label className="block text-sm font-medium text-gray-700">First Name</label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  className={baseInputStyles}
                  required
                />
              </div>
              <div className="space-y-2" id="lastName-section">
                <label className="block text-sm font-medium text-gray-700">Last Name</label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  className={baseInputStyles}
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={baseInputStyles}
                  disabled
                />
                <p className="text-xs text-gray-500">Email address cannot be changed</p>
              </div>
              <div className="space-y-2" id="phone-section">
                <label className="block text-sm font-medium text-gray-700">Phone Number</label>
                <input
                  type="tel"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleInputChange}
                  className={baseInputStyles}
                  placeholder="e.g., 843-560-3110"
                />
              </div>
            </div>

            {/* Add avatar upload in the first login step */}
            {(user.isFirstLogin && currentStep === 1) || !user.isFirstLogin ? (
              <div className="mt-6" id="avatar-section">
                <label className="block text-sm font-medium text-gray-700 mb-2">Profile Photo</label>
                <div className="flex items-center space-x-4">
                  <div className="flex-shrink-0">
                    <SafeAvatar
                      src={avatarFile ? imagePreview : (formData.avatarUrl || user.avatarUrl)}
                      alt="Profile"
                      className="w-20 h-20 rounded-full"
                      size={80}
                      lazy={false} // Don't lazy load since user is actively editing
                    />
                  </div>
                  <div className="flex-1">
                    <div
                      {...getRootProps()}
                      className="border-2 border-dashed rounded-lg p-3 text-center cursor-pointer transition-colors border-gray-300 hover:border-blue-500"
                    >
                      <input {...getInputProps()} />
                      <p className="text-sm text-gray-600">
                        <span className="text-blue-600">Click to upload</span> or drag and drop
                      </p>
                      <p className="text-xs text-gray-500">PNG, JPG, GIF up to 10MB</p>
                    </div>
                    

                    

                  </div>
                </div>
              </div>
            ) : null}

            {/* Add submit button for profile info when in first login flow */}
            {user.isFirstLogin && currentStep === 1 && (
              <div className="mt-6">
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50"
                >
                  {submitting
                    ? 'Saving...'
                    : usedGoogleSso
                      ? 'Save Profile & Continue to Dashboard'
                      : 'Save Profile & Continue to Password'}
                </button>
              </div>
            )}

            {/* Add a save button for returning users to save profile changes */}
            {!user.isFirstLogin && (
              <div className="mt-6">
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50"
                >
                  {submitting ? 'Saving...' : 'Save Profile Changes'}
                </button>
              </div>
            )}

            {/* Add a save button when the user returns to edit their profile after setup */}
            {user.isFirstLogin && currentStep === 1 && (setupCompleted || passwordUpdateSuccess) && (
              <div className="mt-6">
                <div className="flex justify-between gap-4">
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={submitting}
                    className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50"
                  >
                    {submitting ? 'Saving...' : 'Save Profile'}
                  </button>

                  <button
                    type="button"
                    onClick={() => setCurrentStep(2)}
                    className="flex-1 bg-white text-blue-600 border border-blue-300 py-2 px-4 rounded-md hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                  >
                    Return to Password Section
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Job location sharing — all users; field technicians use for daily work */}
      <LocationTrackingSettingsSection user={user} />

      {!user.isFirstLogin && (
        <div className="mb-8 bg-white shadow-sm rounded-lg overflow-hidden border border-gray-200">
          <div className="px-4 py-5 sm:px-6 bg-gray-50 border-b border-gray-200">
            <h3 className="text-lg font-medium leading-6 text-gray-900">Alert Preferences</h3>
            <p className="mt-1 text-sm text-gray-500">
              Choose how you receive alerts enabled for your role
            </p>
          </div>
          <div className="p-6 space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label className="flex items-start rounded-md border border-gray-200 p-4">
                <input
                  type="checkbox"
                  checked={formData.emailNotifications}
                  onChange={() => handleToggleMasterNotification('emailNotifications')}
                  className="mt-1 h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-3">
                  <span className="block text-sm font-medium text-gray-900">Email Notifications</span>
                  <span className="block text-xs text-gray-500">Master switch for all email alerts</span>
                </span>
              </label>

              <label className="flex items-start rounded-md border border-gray-200 p-4">
                <input
                  type="checkbox"
                  checked={formData.smsNotifications}
                  onChange={() => handleToggleMasterNotification('smsNotifications')}
                  className="mt-1 h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-3">
                  <span className="block text-sm font-medium text-gray-900">SMS Notifications</span>
                  <span className="block text-xs text-gray-500">Master switch for all text alerts</span>
                </span>
              </label>
            </div>

            <div className="rounded-md border border-gray-200 p-4">
              <label htmlFor="notification-email-override" className="block text-sm font-medium text-gray-900">
                Notification Email Override
              </label>
              <p className="mt-1 text-xs text-gray-500">
                Route job, work order, and other operational alert emails to a different address.
                Leave blank to use your account email ({user.email}). Password reset and invitation emails always go to your account email.
              </p>
              <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center">
                <input
                  type="email"
                  id="notification-email-override"
                  name="notificationEmailOverride"
                  value={formData.notificationEmailOverride || ''}
                  onChange={handleInputChange}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSaveNotificationEmailOverride();
                    }
                  }}
                  className={baseInputStyles}
                  placeholder="alternate@example.com"
                  aria-label="Notification email override"
                  tabIndex={0}
                />
                <button
                  type="button"
                  onClick={handleSaveNotificationEmailOverride}
                  disabled={isSavingNotificationEmail}
                  className="inline-flex justify-center rounded-md border border-transparent bg-sky-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2 disabled:opacity-50"
                  aria-label="Save notification email override"
                >
                  {isSavingNotificationEmail ? 'Saving...' : 'Save Override'}
                </button>
              </div>
            </div>

            <div>
              <div className="mb-3 flex items-center justify-between">
                <h4 className="text-sm font-medium text-gray-900">Alerts Enabled for Your Role</h4>
                {notificationPreferencesLoading && (
                  <span className="text-xs text-gray-500">Loading...</span>
                )}
              </div>

              {notificationPreferencesError && (
                <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                  {notificationPreferencesError.message}
                </div>
              )}

              {!notificationPreferencesLoading && !notificationPreferencesError && notificationPreferences.length === 0 && (
                <div className="rounded-md bg-gray-50 p-4 text-sm text-gray-500">
                  No role-enabled alerts are currently available for your account.
                </div>
              )}

              {notificationPreferences.length > 0 && (
                <div className="overflow-hidden rounded-lg border border-gray-200">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                          Alert
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                          Email
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                          SMS
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {notificationPreferences.map((preference) => {
                        const emailSavingKey = `${preference.notificationType}:emailEnabled`;
                        const smsSavingKey = `${preference.notificationType}:smsEnabled`;
                        const label = notificationTypeLabels[preference.notificationType] || preference.notificationType.replace(/_/g, ' ');

                        return (
                          <tr key={preference.notificationType}>
                            <td className="px-4 py-4 text-sm font-medium text-gray-900">
                              {label}
                            </td>
                            <td className="px-4 py-4 text-sm text-gray-600">
                              <label className={`inline-flex items-center ${preference.roleEmailEnabled ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}>
                                <input
                                  type="checkbox"
                                  checked={preference.roleEmailEnabled && preference.emailEnabled}
                                  disabled={!preference.roleEmailEnabled || savingNotificationKey === emailSavingKey}
                                  onChange={() => handleToggleAlertPreference(preference, 'emailEnabled')}
                                  className="h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:cursor-not-allowed"
                                />
                                <span className="ml-2">
                                  {savingNotificationKey === emailSavingKey ? 'Saving...' : (preference.roleEmailEnabled && preference.emailEnabled ? 'On' : 'Off')}
                                </span>
                              </label>
                            </td>
                            <td className="px-4 py-4 text-sm text-gray-600">
                              <label className={`inline-flex items-center ${preference.roleSmsEnabled ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}>
                                <input
                                  type="checkbox"
                                  checked={preference.roleSmsEnabled && preference.smsEnabled}
                                  disabled={!preference.roleSmsEnabled || savingNotificationKey === smsSavingKey}
                                  onChange={() => handleToggleAlertPreference(preference, 'smsEnabled')}
                                  className="h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:cursor-not-allowed"
                                />
                                <span className="ml-2">
                                  {savingNotificationKey === smsSavingKey ? 'Saving...' : (preference.roleSmsEnabled && preference.smsEnabled ? 'On' : 'Off')}
                                </span>
                              </label>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Password Change Section - Show based on step for first login users */}
      {(currentStep === 2 && user.isFirstLogin) && (
        <div id="password-section" className="mb-8 bg-white shadow-sm rounded-lg overflow-hidden border border-blue-300">
          <div className="px-4 py-5 sm:px-6 bg-blue-50 border-b border-blue-200">
            {setupCompleted || passwordUpdateSuccess ? (
              <>
                <h3 className="text-lg font-medium leading-6 text-blue-800">Password Management</h3>
                <p className="mt-1 text-sm text-blue-600">
                  Change your password or continue to dashboard
                </p>
              </>
            ) : (
              <>
                <h3 className="text-lg font-medium leading-6 text-blue-800">Set Your New Password</h3>
                <p className="mt-1 text-sm text-blue-600">
                  Please create a new secure password for your account
                </p>
              </>
            )}
          </div>
          <div className="p-6">
            {setupCompleted || passwordUpdateSuccess ? (
              <div className="space-y-6">
                <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded-md">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-lg font-medium text-green-800">Your Profile Is Complete!</h3>
                      <p className="text-green-700 mt-2">
                        You have successfully completed your profile setup and can now access the dashboard.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col space-y-3">
                  <button
                    type="button"
                    onClick={goToDashboard}
                    className="w-full bg-blue-600 text-white py-3 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors font-medium"
                  >
                    Go to Dashboard
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setSetupCompleted(false);
                      setPasswordUpdateSuccess(false);
                      setCurrentStep(1);
                      setNeedsPasswordChange(false);
                    }}
                    className="w-full bg-white text-blue-600 border border-blue-300 py-3 px-4 rounded-md hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors font-medium"
                  >
                    Return to Profile Settings
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handlePasswordChangeSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">New Password</label>
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className={baseInputStyles}
                      placeholder="Enter a secure password"
                      minLength={8}
                      required
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Password must be at least 8 characters
                    </p>
                  </div>
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Confirm Password</label>
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className={baseInputStyles}
                      placeholder="Confirm your password"
                      required
                    />
                  </div>
                </div>
                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={passwordChangeLoading}
                    className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50"
                  >
                    {passwordChangeLoading ? 'Updating...' : 'Update Password'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Admin Override Section - Only visible for admins with incomplete profile and no password change needed */}
      {user?.role === 'ADMIN' && !validationState.isComplete && !needsPasswordChange && (
        <div className="mb-6 bg-white shadow-sm rounded-lg overflow-hidden border border-orange-200">
          <div className="px-4 py-5 sm:px-6 bg-orange-50 border-b border-orange-100">
            <h3 className="text-lg font-medium leading-6 text-orange-800">Admin Override</h3>
            <p className="mt-1 text-sm text-orange-600">
              As an admin, you can bypass the profile completion requirement if needed
            </p>
          </div>
          <div className="p-4">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <p className="text-sm text-gray-700">
                This will allow you to access the dashboard, but we still recommend completing your profile.
              </p>
              <button
                type="button"
                onClick={handleAdminOverride}
                className="flex-shrink-0 px-4 py-2 border border-orange-300 rounded-md shadow-sm text-sm font-medium text-orange-700 bg-white hover:bg-orange-50"
              >
                Skip Profile Setup
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error message */}
      {updateError && (
        <div className="mb-6 rounded-lg bg-red-50 p-4 text-sm text-red-600 border border-red-200">
          {updateError.message}
        </div>
      )}

      {/* Success message */}
      {updateSuccess && (
        <div className="mb-6 rounded-lg bg-green-50 p-4 text-sm text-green-600 border border-green-200">
          Your profile has been updated successfully.
        </div>
      )}

      {/* Form - Remove duplicate Personal Information section and keep only the Avatar section */}
      <form onSubmit={handleSubmit} className={`space-y-8 ${needsPasswordChange ? "opacity-50 pointer-events-none" : ""}`}>
        {/* Remove the duplicate avatar section */}

        {/* Google Authentication Banner for returning users using SSO */}
        {!user.isFirstLogin && user.lastAuthMethod === 'google_sso' && !showPasswordSwitch && (
          <div className="bg-white shadow-sm rounded-lg overflow-hidden border border-blue-200">
            <div className="px-4 py-5 sm:px-6 bg-blue-50 border-b border-blue-200">
              <div className="flex items-center">
                <svg className="h-6 w-6 mr-3 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                <div>
                  <h3 className="text-lg font-medium leading-6 text-blue-800">Google Authentication</h3>
                  <p className="mt-1 text-sm text-blue-600">
                    This account uses Google Authentication for sign-in. Password management is handled through your Google account.
                  </p>
                </div>
              </div>
            </div>
            <div className="p-6">
              <p className="text-sm text-gray-600 mb-4">
                You are currently signed in using Google. To change your password, visit your Google Account settings. 
                If you'd like to switch back to using a regular password for this app, click below.
              </p>
              <button
                type="button"
                onClick={handleSwitchToPassword}
                disabled={switchingAuth}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50"
              >
                {switchingAuth ? 'Switching...' : 'Switch to password login'}
              </button>
            </div>
          </div>
        )}

        {/* Password Change Section for regular users (hidden when using Google SSO) */}
        {!user.isFirstLogin && (user.lastAuthMethod !== 'google_sso' || showPasswordSwitch) && (
          <div className="bg-white shadow-sm rounded-lg overflow-hidden border border-gray-200">
            <div className="px-4 py-5 sm:px-6 bg-gray-50 border-b border-gray-200">
              <h3 className="text-lg font-medium leading-6 text-gray-900">Change Password</h3>
              <p className="mt-1 text-sm text-gray-500">
                Update your password to maintain account security
              </p>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 gap-6">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Current Password</label>
                  <input
                    type="password"
                    name="currentPassword"
                    value={formData.currentPassword || ''}
                    onChange={handleInputChange}
                    className={baseInputStyles}
                    placeholder="Enter your current password"
                  />
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">New Password</label>
                  <input
                    type="password"
                    name="newPassword"
                    value={formData.newPassword || ''}
                    onChange={handleInputChange}
                    className={baseInputStyles}
                    placeholder="Enter a new password"
                    minLength={8}
                  />
                  <p className="text-xs text-gray-500">Password must be at least 8 characters</p>
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Confirm New Password</label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={formData.confirmPassword || ''}
                    onChange={handleInputChange}
                    className={`${baseInputStyles} ${!passwordsMatch && formData.confirmPassword ? 'border-red-500' : ''}`}
                    placeholder="Confirm your new password"
                  />
                  {!passwordsMatch && formData.confirmPassword && (
                    <p className="text-xs text-red-500">Passwords don't match</p>
                  )}
                </div>
                <div>
                  <button
                    type="button"
                    onClick={handlePasswordChange}
                    disabled={regularPasswordLoading}
                    className="w-full md:w-auto bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50"
                  >
                    {regularPasswordLoading ? 'Updating...' : 'Update Password'}
                  </button>
                </div>
                {passwordError && (
                  <div className="text-sm text-red-600">
                    {passwordError.message || "Failed to update password"}
                  </div>
                )}
                {regularPasswordSuccess && (
                  <div className="text-sm text-green-600">
                    Password updated successfully
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </form>
    </div>
  );
} 
